'use strict';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QueryBuilder } from '@common/model/query-builder.model';
import { ReadService } from '@common/service/read.service';
import { LanguageService } from '@core/service/language.service';
import { HabilitationService } from '@core/service/habilitation.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ResourceEditComponent } from '../habilitation/resource-edit-dialog.component';

const noop = () => {
  return;
};

export interface SourceType {
  query: string;
  [key: string]: any;
}

const COMPLETER_CONTROL_VALUE_ACCESSOR = {
  multi: true,
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TmwDropdownCmp),
};

@Component({
  selector: 'tmw-dropdown',
  templateUrl: './dropdown.component.html',
  standalone: true,
  providers: [COMPLETER_CONTROL_VALUE_ACCESSOR],
  imports: [CommonModule, FormsModule],
})
export class TmwDropdownCmp implements ControlValueAccessor, OnInit {
  readService = inject(ReadService);
  service = inject(HabilitationService);
  modalService = inject(NgbModal);
  private readonly destroy: DestroyRef = inject(DestroyRef);
  private readonly languageService: LanguageService = inject(LanguageService);
  enableEdit = this.service.enableEdit;

  @Input() id: string = '';
  @Input() name = '';
  @Input() isRequired = false;
  @Input() exclude: any;
  @Input() showCode = false;
  defaultFormater = (lang: string, o: any) => {
    const libelle = lang == 'ar' ? o.libelleAr : o.libelle;
    return this.showCode && o.code ? `${o.code} - ${libelle}` : libelle;
  };
  @Input() format: any = this.defaultFormater;
  disableInput = false;
  @Input() unique = false;

  // @Input({ required: true }) public config!: SelectConfig;

  @Input() setDefault: boolean = false;
  @Input() initChange = false;
  @Input() runIf = true;
  @Input() textDefault = '';

  dataList: any[] = [];
  val: any;
  rawResult: any[] = [];
  @Input() typeRessource?: string;
  refCode?: string;

  lang: string = this.languageService.currentLang$.value;

  searchText: string = '';
  isOpen: boolean = false;
  activeIndex: number = -1;

  @Output() public selected = new EventEmitter<any>();

  private onTouch: () => void = noop;
  private onChange: (_: any) => void = noop;

  constructor() {}

  ngOnInit() {
    this.languageService
      .getCurrent()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((lang) => {
        this.lang = lang;
        this.handleResponse();
      });
  }

  get filteredList(): any[] {
    if (!this.searchText) return this.dataList;
    const lower = this.searchText.toLowerCase();
    return this.dataList.filter((d) => d.formated?.toLowerCase().includes(lower));
  }

  get value() {
    return this.val;
  }
  set value(val) {
    if (this.val !== val) {
      this.val = val;
      this.selected.emit(this.getSelected());
      this.onChange(this.val);
      this.onTouch();
    }
  }

  public writeValue(value: any) {
    this.val = value;
    this.syncSearchText();
  }

  private syncSearchText() {
    if (this.val != null && this.dataList.length) {
      const item = this.dataList.find((d) => d.id == this.val);
      this.searchText = item ? item.formated : '';
    } else if (!this.val) {
      this.searchText = '';
    }
  }

  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disableInput = isDisabled;
  }

  onInputChange() {
    this.isOpen = true;
    this.activeIndex = -1;
    if (!this.searchText) {
      this.value = undefined;
    }
  }

  selectItem(item: any) {
    this.value = item.id;
    this.searchText = item.formated;
    this.isOpen = false;
    this.activeIndex = -1;
  }

  clearSelection() {
    this.value = undefined;
    this.searchText = '';
    this.isOpen = false;
  }

  onFocus() {
    this.isOpen = true;
    this.activeIndex = -1;
  }

  onBlur() {
    setTimeout(() => {
      this.isOpen = false;
      if (this.val == null) {
        this.searchText = '';
      } else {
        const item = this.dataList.find((d) => d.id == this.val);
        this.searchText = item ? item.formated : '';
      }
    }, 150);
  }

  onKeyDown(event: KeyboardEvent) {
    const list = this.filteredList;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.isOpen = true;
      this.activeIndex = Math.min(this.activeIndex + 1, list.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.selectItem(list[this.activeIndex]);
    } else if (event.key === 'Escape') {
      this.isOpen = false;
      this.activeIndex = -1;
    }
  }

  @Input()
  public set refType(code: string) {
    this.typeRessource= 'Referentiel';
    this.refCode = code;
    this.query = { query: 'Referentiel', code };
  }
  @Input()
  public set query(source: SourceType) {
    if (source?.query && this.runIf) {
      let params: any = {};
      Object.keys(source).forEach((k: any) => {
        if (source[k]!=null && source[k]!=undefined) {
          params[k] = source[k];
        }
      });

      const queryBuilder: QueryBuilder = { params };
      this.readService
        .query(source.query, queryBuilder)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe({
          next: (result: any) => {
            this.rawResult = result || [];
            this.handleResponse();
          },
        });
    }
  }

  handleResponse() {
    if (!this.rawResult.length) {
      this.dataList = [];
      this.searchText = '';
      return;
    }
    this.dataList = this.rawResult
      .filter((d: any) => d != null && d !== '' && d.id != null && d.id !== '' && this.format(this.lang, d)?.trim() !== '')
      .map((d: any) => ({ ...d, formated: this.format(this.lang, d) }));

    if (this.dataList.length) {
      this.applyDefaultSelection();
      if (this.exclude) {
        this.dataList = this.dataList.filter((data) => data.id !== this.exclude);
      }
    }
  }

  private applyDefaultSelection() {
    if (this.unique && this.dataList.length === 1) {
      this.value = this.dataList[0].id;
      this.searchText = this.dataList[0].formated;
      this.selected.emit(this.getSelected());
    } else if (this.dataList.length > 1 && !this.val && this.setDefault) {
      this.applyDefaultItem();
    } else if (this.value) {
      this.syncCurrentSelection();
    }
  }

  private applyDefaultItem() {
    const defaultItem = this.dataList.find((d) => d.default);
    if (defaultItem) {
      this.value = defaultItem.id;
      this.searchText = defaultItem.formated;
      if (this.initChange) {
        this.onChange(this.value);
      }
    }
  }

  private syncCurrentSelection() {
    const selectedItem = this.dataList.find((d) => d.id == this.value);
    if (selectedItem) {
      this.searchText = selectedItem.formated;
    } else {
      this.value = undefined;
      this.searchText = '';
      this.onChange(this.value);
    }
  }

  public getDataList() {
    return this.dataList;
  }

  public getSelected() {
    let result: any = {};
    if (this.dataList && this.val) {
      result = this.dataList.find((e) => e.id == this.val);
    }
    return result;
  }

  edit(e: any) {
    e.stopPropagation();

    const modalRef = this.modalService.open(ResourceEditComponent, {
      size: 'xl',
      backdrop: 'static',
      windowClass: 'modal show',
    });
    modalRef.componentInstance.habilitationParams = {
      typeRessource: this.typeRessource,
      value: this.refCode||'0'
    };
  }
}
